import { useCallback, useState } from "react";
import { reviewApi, type ReviewDetail } from "../../../api/clock";
import { Drawer } from "../../../components/Overlay";
import { fmtDate } from "../../../utils/labels";
import { LoadState, useLoad } from "../shared";
import { EvidenceMap } from "./EvidenceMap";
import { ResolutionForm } from "./ResolutionForm";

const H = ({ children }: { children: string }) => <h4 className="label-caps mb-2 mt-5 text-zinc-500">{children}</h4>;
type Attempt = { at: string; outcome: string; accuracy_m: number | null; distance_m: number | null };

function Attempts({ reqs, ev }: { reqs: { attempts: Attempt[]; type: string }[]; ev: { outcome: string; position_timestamp?: string; received_at: string }[] }) {
  const rows = reqs.flatMap((r) => r.attempts.map((a) => ({ ...a, type: r.type })));
  if (!rows.length) return <p data-testid="review-attempts" className="text-xs text-zinc-500">No GPS attempts.</p>;
  return (
    <table data-testid="review-attempts" className="w-full text-left text-xs text-zinc-300">
      <tbody>{rows.map((a, i) => {
        const e = ev[i];
        const fresh = e?.position_timestamp ? Math.round((new Date(e.received_at).getTime() - new Date(e.position_timestamp).getTime()) / 1000) : null;
        return <tr key={i} className="border-t border-white/5"><td className="py-1.5 font-mono">{a.type}</td><td className={a.outcome === "PASS" ? "text-emerald-300" : "text-warn"}>{a.outcome}</td>
          <td>±{a.accuracy_m ?? "—"} m</td><td>{a.distance_m ?? "—"} m away</td><td>{fresh != null ? `fix ${fresh}s old` : ""}</td><td className="text-zinc-500">{fmtDate(a.at)}</td></tr>;
      })}</tbody>
    </table>
  );
}

function Body({ d, onDone }: { d: ReviewDetail; onDone: () => void }) {
  const it = d.item;
  const reqs = d.punch_request ? [d.punch_request] : d.punch_requests ?? [];
  const ses = d.kind === "SESSION" ? it : d.session;
  return (
    <div data-testid="review-detail" className="text-sm text-zinc-300">
      <p className="font-display text-2xl font-bold uppercase text-white">{d.employee_name}</p>
      <p className="font-mono text-xs text-warn">{it.code ?? (it.review_flags ?? []).join(", ") ?? ""} {it.type ?? ""}</p>
      {d.received_local && <p className="mt-1 text-xs">Text received {d.received_local}</p>}
      {d.sms && (<><H>SMS evidence</H>
        <div data-testid="review-sms" className="rounded-sm border border-white/10 p-3 text-xs">
          <p className="font-mono text-white">"{d.sms.body}"</p>
          <p className="mt-1">Received {d.sms.received_local} · UTC {String(d.sms.received_at)}</p>
          <p className="font-mono text-zinc-500">MessageSid {d.sms.provider_message_id ?? d.sms.message_sid ?? "—"}</p>
        </div></>)}
      {d.kind === "CORRECTION" && (<><H>Employee correction request</H>
        <div data-testid="review-correction" className="rounded-sm border border-white/10 p-3 text-xs">
          <p className="text-white">{it.requested_values.kind} · {d.shift.label}</p>
          <p>Requested IN: {it.requested_values.requested_in_local ?? (it.requested_values.requested_out_local ? "—" : it.requested_values.proposed_local ?? "—")} · OUT: {it.requested_values.requested_out_local ?? "—"}</p>
          <p className="text-zinc-500">Leave the times empty to approve exactly as requested; changing them marks it “approved with changes”.</p>
          {(it.messages ?? []).length > 0 && <ul data-testid="review-correction-thread" className="mt-2 space-y-1 border-l border-white/10 pl-2">{(it.messages ?? []).map((m: { from: string; text: string; kind?: string }, i: number) => <li key={i}><strong className="text-white">{m.from === "ADMIN" ? "Admin" : "Employee"}</strong>{m.kind ? ` (${m.kind.toLowerCase()})` : ""}: {m.text}</li>)}</ul>}
          <p className="mt-1">Original values: <span className="font-mono">{JSON.stringify(it.original_values)}</span></p>
        </div></>)}
      {(it.employee_note || it.reason) && (<><H>Employee note</H><p data-testid="review-employee-note" className="rounded-sm bg-ink p-3 text-xs">{it.employee_note ?? it.reason}</p></>)}
      <H>GPS attempts</H><Attempts reqs={reqs} ev={d.evidence ?? []} />
      <H>Location (admin only)</H><EvidenceMap venue={d.venue} evidence={d.evidence ?? []} purged={!!d.evidence_purged} />
      {ses && (<><H>Session</H>
        <dl data-testid="review-session" className="grid grid-cols-2 gap-1 text-xs">
          <dt className="text-zinc-500">Status</dt><dd>{ses.status}</dd>
          <dt className="text-zinc-500">IN text / GPS</dt><dd>{ses.in_sms_received_at_local || "—"} / {ses.in_gps_verified_at_local || "—"}</dd>
          <dt className="text-zinc-500">OUT text / GPS</dt><dd>{ses.out_sms_received_at_local || "—"} / {ses.out_gps_verified_at_local || "—"}</dd>
          <dt className="text-zinc-500">Payable in / out</dt><dd>{ses.payable_in_at_local || "—"} / {ses.payable_out_at_local || "—"}</dd>
        </dl></>)}
      <H>Resolve</H><ResolutionForm d={d} onDone={onDone} />
      <H>Audit trail</H>
      <ol data-testid="review-audit" className="space-y-1 text-[11px]">
        {d.audit.map((a, i) => <li key={i} className="border-l border-white/10 pl-3"><span className="font-mono text-zinc-200">{a.action}</span> · {a.actor_type} · {fmtDate(a.occurred_at)}{a.metadata?.note ? ` · "${a.metadata.note}"` : ""}{a.metadata?.reason ? ` · "${a.metadata.reason}"` : ""}</li>)}
      </ol>
    </div>
  );
}

export function ReviewDrawer({ kind, id, onClose, onChanged }: { kind: string; id: string; onClose: () => void; onChanged: () => void }) {
  const [v, setV] = useState(0);
  const loader = useCallback(() => reviewApi.detail(kind, id), [kind, id, v]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<ReviewDetail>(loader);
  return (
    <Drawer open title="Review item" onClose={onClose} testId="review-drawer">
      <LoadState error={error} loading={!data && !error} testId="review-detail-load" />
      {data && <Body key={`${data.item.id}-${v}`} d={data} onDone={() => { setV((x) => x + 1); onChanged(); }} />}
    </Drawer>
  );
}
